import React, {useRef} from 'react';
import {TextInput, View} from 'react-native';
import {noop} from 'lodash';

import Text from '/component/base/Text';
import {Modal} from '/constant/TestIDs';
import {getTextFromReactNode} from '/util/accessibility.util';
import testProperties from '/util/testing/testProperties';

import {Cell} from './component';
import {DARK, LIGHT, PIN_LENGTH, PIN_STATUS} from './PinInput.constant';
import {usePinInput} from './PinInput.hook';
import {stylesThemed} from './PinInput.style';

// Define props interface
interface PinInputProps {
  theme: typeof LIGHT | typeof DARK;
  textVariant: string;
  title: string;
  onInputComplete: (pin: string) => void;
  onInputPin: (pin: string) => void;
  status: (typeof PIN_STATUS)[keyof typeof PIN_STATUS];
  autoFocus: boolean;
}

export const PinInput: React.FC<PinInputProps> = ({
  theme = LIGHT,
  textVariant = '',
  title = '',
  onInputComplete = noop,
  onInputPin = noop,
  status = PIN_STATUS.initial,
  autoFocus = true,
}) => {
  const styles = stylesThemed(theme);
  const inputRef = useRef<TextInput>(null);

  const {
    onBlur,
    onFocus,
    onChangeText,
    isWrongStyle,
    isWrongPin,
    slidesUpDownAnimations,
    value,
    focused,
  } = usePinInput({theme, onInputComplete, onInputPin});

  const renderCell = () => {
    return Array.from({length: PIN_LENGTH}, (_, id) => (
      <Cell
        key={id}
        isWrongPin={isWrongPin}
        id={id}
        focused={focused}
        valueLength={value.length}
        styles={styles}
        isWrongStyle={isWrongStyle}
        slidesUpDownAnimations={slidesUpDownAnimations}
      />
    ));
  };
  return (
    <View style={styles.container}>
      <Text
        style={styles.text}
        variant={textVariant}
        accessible={true}
        accessibilityLabel={getTextFromReactNode(title)}>
        {title}
      </Text>
      <View
        {...testProperties(Modal.pinInputElement)}
        style={styles.textInputContainer}>
        <View style={styles.innerContainer}>{renderCell()}</View>
        <TextInput
          value={value}
          testID="pin-input-field"
          ref={inputRef}
          autoFocus={autoFocus}
          keyboardAppearance="light"
          onChangeText={onChangeText}
          onFocus={onFocus}
          onBlur={onBlur}
          spellCheck={false}
          keyboardType="number-pad"
          numberOfLines={1}
          caretHidden
          maxLength={PIN_LENGTH}
          style={styles.input}
          editable={status !== PIN_STATUS.enter}
          accessible={true}
          accessibilityLabel="Enter your 4-digit PIN"
          accessibilityHint="Type your PIN using the number pad"
        />
      </View>
    </View>
  );
};
